import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Pagination,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DropzoneGlobal from "components/DropzoneGlobal";
import {
  FOTOS_ACCENT,
  fotosFieldSx,
  fotosFilterChipSx,
  fotosSectionSx,
  fotosToolbarSx,
} from "./fotos-evento.theme";
import FormField from "layouts/pages/account/components/FormField";
import { setMessage, useMaterialUIController } from "context";
import {
  currencyMaskBRL,
  currencyToNumber,
  formatCurrency,
} from "helpers/utils";
import { IEventoResponse } from "interfaces/eventos";
import type {
  IFotoEventoGerenciar,
  IEventoVendaFoto,
  IRelatorioRepasseFotos,
} from "interfaces/eventoFoto";
import {
  excluirFotoEvento,
  listarFotosEventoGerenciar,
  obterRelatorioRepasseFotos,
  obterVendaFotoEvento,
  uploadFotosEvento,
  upsertVendaFotoEvento,
} from "services/eventoFoto";

interface Props {
  evento: IEventoResponse;
}

type FiltroAtivo = "todas" | "ativas" | "inativas";

const LIMIT = 24;

const schemaPrecos = Yup.object({
  precoBaixa: Yup.string().required("Informe o preço baixa"),
  precoMedia: Yup.string().required("Informe o preço média"),
  precoAlta: Yup.string().required("Informe o preço alta"),
  ativo: Yup.boolean(),
});

function priceToDigits(value: number | string | undefined | null): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(Math.round(n * 100));
}

function digitsToDisplay(digits: string): string {
  return currencyMaskBRL(digits || "0");
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? FOTOS_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

const GALERIA_FILTROS: Array<{ key: FiltroAtivo; label: string }> = [
  { key: "todas", label: "Todas" },
  { key: "ativas", label: "Ativas" },
  { key: "inativas", label: "Inativas" },
];

export default function EventoFoto({ evento }: Props) {
  const [, dispatch] = useMaterialUIController();

  const [vendaConfig, setVendaConfig] = useState<IEventoVendaFoto | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [fotos, setFotos] = useState<IFotoEventoGerenciar[]>([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loadingFotos, setLoadingFotos] = useState(true);
  const [filtro, setFiltro] = useState<FiltroAtivo>("todas");
  const [page, setPage] = useState(1);
  const [arquivosUpload, setArquivosUpload] = useState<File[]>([]);
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [excluindoId, setExcluindoId] = useState<string | null>(null);
  const [relatorioRepasse, setRelatorioRepasse] =
    useState<IRelatorioRepasseFotos | null>(null);
  const [loadingRelatorio, setLoadingRelatorio] = useState(true);

  const filtroAtivoParam = useMemo(() => {
    if (filtro === "ativas") return true;
    if (filtro === "inativas") return false;
    return undefined;
  }, [filtro]);

  const loadConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const cfg = await obterVendaFotoEvento(evento.id);
      setVendaConfig(cfg);
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text:
          error instanceof Error
            ? error.message
            : "Falha ao carregar configuração de preços.",
      });
    } finally {
      setLoadingConfig(false);
    }
  }, [dispatch, evento.id]);

  const loadFotos = useCallback(async () => {
    setLoadingFotos(true);
    try {
      const resp = await listarFotosEventoGerenciar(evento.id, {
        page,
        limit: LIMIT,
        ativo: filtroAtivoParam,
      });
      setFotos(resp.data);
      setMeta({
        page: resp.meta.page,
        totalPages: resp.meta.totalPages || 1,
        total: resp.meta.total,
      });
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text:
          error instanceof Error ? error.message : "Falha ao carregar fotos.",
      });
    } finally {
      setLoadingFotos(false);
    }
  }, [dispatch, evento.id, filtroAtivoParam, page]);

  const loadRelatorioRepasse = useCallback(async () => {
    setLoadingRelatorio(true);
    try {
      setRelatorioRepasse(await obterRelatorioRepasseFotos(evento.id));
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text:
          error instanceof Error
            ? error.message
            : "Falha ao carregar o demonstrativo de repasses.",
      });
    } finally {
      setLoadingRelatorio(false);
    }
  }, [dispatch, evento.id]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    loadFotos();
  }, [loadFotos]);

  useEffect(() => {
    loadRelatorioRepasse();
  }, [loadRelatorioRepasse]);

  useEffect(() => {
    setPage(1);
  }, [filtro]);

  const handleUpload = async () => {
    if (arquivosUpload.length === 0) {
      setMessage(dispatch, {
        open: true,
        severity: "warning",
        text: "Selecione ao menos uma imagem.",
      });
      return;
    }

    setEnviando(true);
    try {
      const result = await uploadFotosEvento(evento.id, arquivosUpload);
      const msg =
        result.erros.length > 0
          ? `${result.total} foto(s) enviada(s). ${result.erros.length} falha(s).`
          : `${result.total} foto(s) enviada(s) com sucesso.`;

      setMessage(dispatch, {
        open: true,
        severity: result.erros.length > 0 ? "warning" : "success",
        text: msg,
      });

      setArquivosUpload([]);
      setDropzoneKey((k) => k + 1);
      loadFotos();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha no upload.",
      });
    } finally {
      setEnviando(false);
    }
  };

  const handleExcluir = async (foto: IFotoEventoGerenciar) => {
    if (
      !window.confirm(
        `Excluir a foto "${foto.nomeArquivo}"? Esta ação não pode ser desfeita.`
      )
    ) {
      return;
    }

    setExcluindoId(foto.id);
    try {
      await excluirFotoEvento(evento.id, foto.id);
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Foto excluída com sucesso.",
      });
      if (fotos.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        loadFotos();
      }
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao excluir foto.",
      });
    } finally {
      setExcluindoId(null);
    }
  };

  const initialPrecos = useMemo(
    () => ({
      precoBaixa: priceToDigits(vendaConfig?.precoBaixa),
      precoMedia: priceToDigits(vendaConfig?.precoMedia),
      precoAlta: priceToDigits(vendaConfig?.precoAlta),
      ativo: vendaConfig?.ativo ?? true,
    }),
    [vendaConfig]
  );

  return (
    <MDBox>
      <Box sx={fotosToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PhotoLibraryOutlinedIcon sx={{ color: FOTOS_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Fotos do evento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {vendaConfig?.ativo ? (
              <Chip label="Venda ativa" color="success" size="small" />
            ) : (
              <Chip label="Venda inativa" variant="outlined" size="small" />
            )}
          </Stack>
        </Stack>
      </Box>

      {loadingFotos && !meta.total && loadingRelatorio && !relatorioRepasse ? (
        <Grid container spacing={2} mb={2.5}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={6} sm={4} key={i}>
              <Skeleton variant="rounded" height={88} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} mb={2.5}>
          <Grid item xs={6} sm={4}>
            <StatCard label="Total na galeria" value={meta.total} accent />
          </Grid>
          <Grid item xs={6} sm={4}>
            <StatCard
              label="Fotos vendidas"
              value={relatorioRepasse?.resumo.quantidadeFotosVendidas ?? 0}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              label="Total vendido"
              value={formatCurrency(
                relatorioRepasse?.resumo.totalBrutoVendido ?? 0,
              )}
            />
          </Grid>
        </Grid>
      )}

      <Box sx={{ ...fotosSectionSx, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
          Preços de venda
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Valores por resolução (baixa, média e alta). Preview com marca
          d&apos;água; arquivo final liberado após pagamento.
        </Typography>

        {loadingConfig ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialPrecos}
            validationSchema={schemaPrecos}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const saved = await upsertVendaFotoEvento(evento.id, {
                  precoBaixa: currencyToNumber(values.precoBaixa),
                  precoMedia: currencyToNumber(values.precoMedia),
                  precoAlta: currencyToNumber(values.precoAlta),
                  ativo: values.ativo,
                });
                setVendaConfig(saved);
                setMessage(dispatch, {
                  open: true,
                  severity: "success",
                  text: "Preços salvos com sucesso.",
                });
              } catch (error: unknown) {
                setMessage(dispatch, {
                  open: true,
                  severity: "error",
                  text:
                    error instanceof Error
                      ? error.message
                      : "Falha ao salvar preços.",
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <Form>
                <Grid container spacing={2}>
                  {(
                    [
                      ["precoBaixa", "Resolução baixa (800px)"],
                      ["precoMedia", "Resolução média (1600px)"],
                      ["precoAlta", "Resolução alta (até 4000px)"],
                    ] as const
                  ).map(([field, label]) => (
                    <Grid item xs={12} md={4} key={field}>
                      <FormField
                        label={label}
                        type="text"
                        name={field}
                        value={digitsToDisplay(values[field])}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue(
                            field,
                            (e.target.value || "").replace(/\D/g, ""),
                          )
                        }
                        variant="outlined"
                        fullWidth
                        sx={fotosFieldSx}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.ativo}
                          onChange={(e) =>
                            setFieldValue("ativo", e.target.checked)
                          }
                          color="warning"
                        />
                      }
                      label="Venda de fotos ativa neste evento"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <MDButton
                        type="submit"
                        variant="gradient"
                        color="dark"
                        disabled={isSubmitting}
                        sx={{
                          flexShrink: 0,
                          background: `linear-gradient(195deg, ${FOTOS_ACCENT}, #e04a1a)`,
                        }}
                      >
                        {isSubmitting ? "Salvando…" : "Salvar preços"}
                      </MDButton>
                      {vendaConfig ? (
                        <Typography variant="caption" color="text.secondary">
                          Atual: baixa{" "}
                          {formatCurrency(Number(vendaConfig.precoBaixa))}
                          {" · "}
                          média {formatCurrency(Number(vendaConfig.precoMedia))}
                          {" · "}
                          alta {formatCurrency(Number(vendaConfig.precoAlta))}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        )}
      </Box>

      <Box sx={{ ...fotosSectionSx, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
          mb={2}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Demonstrativo de vendas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Considera somente pedidos pagos ou concluídos.
            </Typography>
          </Box>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            disabled={loadingRelatorio}
            onClick={() => void loadRelatorioRepasse()}
            startIcon={<RefreshOutlinedIcon />}
            sx={{ flexShrink: 0 }}
          >
            Atualizar
          </MDButton>
        </Stack>

        {loadingRelatorio && !relatorioRepasse ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : relatorioRepasse && relatorioRepasse.fotografos.length > 0 ? (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "70%" }} />
                <col style={{ width: "30%" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Fotógrafo</TableCell>
                  <TableCell align="right">Fotos vendidas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {relatorioRepasse.fotografos.map((linha) => (
                  <TableRow
                    key={linha.fotografo}
                    hover
                    sx={{
                      "&:hover": { bgcolor: "rgba(249, 96, 48, 0.04)" },
                    }}
                  >
                    <TableCell>{linha.fotografo}</TableCell>
                    <TableCell align="right">
                      {linha.quantidadeFotosVendidas}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            Ainda não há vendas de fotos pagas para este evento.
          </Alert>
        )}
      </Box>

      <Box sx={{ ...fotosSectionSx, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          <CloudUploadOutlinedIcon sx={{ fontSize: 20, color: FOTOS_ACCENT }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Upload de fotos
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Envie até 50 imagens por lote (máx. 25 MB cada e 250 MB no total).
          JPG, PNG ou WebP.
        </Typography>

        <DropzoneGlobal
          key={dropzoneKey}
          accept="only-images"
          maxFiles={50}
          onFilesChange={setArquivosUpload}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          mt={2}
        >
          <MDButton
            variant="gradient"
            color="dark"
            disabled={enviando || arquivosUpload.length === 0}
            onClick={() => void handleUpload()}
            startIcon={<CloudUploadOutlinedIcon />}
            sx={{
              flexShrink: 0,
              background: `linear-gradient(195deg, ${FOTOS_ACCENT}, #e04a1a)`,
            }}
          >
            {enviando
              ? "Enviando…"
              : `Enviar ${arquivosUpload.length || ""} foto(s)`}
          </MDButton>
          {arquivosUpload.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {arquivosUpload.length} arquivo(s) selecionado(s)
            </Typography>
          ) : null}
        </Stack>
      </Box>

      <Box sx={fotosSectionSx}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Galeria ({meta.total})
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {GALERIA_FILTROS.map((item) => (
              <Chip
                key={item.key}
                clickable
                label={item.label}
                onClick={() => setFiltro(item.key)}
                variant="outlined"
                sx={fotosFilterChipSx(filtro === item.key)}
              />
            ))}
          </Stack>
        </Stack>

        {loadingFotos ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton variant="rounded" height={240} />
              </Grid>
            ))}
          </Grid>
        ) : fotos.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "grey.300",
              bgcolor: "grey.50",
            }}
          >
            <PhotoLibraryOutlinedIcon
              sx={{ fontSize: 44, color: "grey.400", mb: 1 }}
            />
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Nenhuma foto na galeria
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Faça upload de imagens para começar a vender fotos deste evento.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {fotos.map((foto) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={foto.id}
                  sx={{ minWidth: 0 }}
                >
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      overflow: "hidden",
                      opacity: foto.ativa ? 1 : 0.85,
                      borderColor: foto.ativa ? "grey.200" : "warning.light",
                      transition: "box-shadow 0.2s",
                      "&:hover": {
                        boxShadow: `0 4px 12px rgba(249, 96, 48, 0.12)`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: 200,
                        bgcolor: "grey.100",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={foto.previewUrl || foto.urlPreview}
                        alt="Foto do evento"
                        loading="lazy"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {!foto.ativa ? (
                        <Chip
                          label="Inativa"
                          size="small"
                          color="warning"
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            fontWeight: 600,
                          }}
                        />
                      ) : null}
                      <IconButton
                        size="small"
                        title="Excluir foto"
                        disabled={excluindoId === foto.id}
                        onClick={() => void handleExcluir(foto)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(0,0,0,0.55)",
                          color: "common.white",
                          "&:hover": { bgcolor: "error.main" },
                        }}
                      >
                        {excluindoId === foto.id ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>

                    <MDBox
                      px={1.5}
                      py={1.5}
                      sx={{ minWidth: 0, overflow: "hidden" }}
                    >
                      {foto.fotografo ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          title={`Fotógrafo: ${foto.fotografo}`}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Fotógrafo:{" "}
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: "text.primary" }}
                          >
                            {foto.fotografo}
                          </Box>
                        </Typography>
                      ) : null}
                      {foto.enviadoPor ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          title={`Enviado por: ${foto.enviadoPor.nome}`}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Enviado por: {foto.enviadoPor.nome}
                        </Typography>
                      ) : null}
                    </MDBox>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {meta.totalPages > 1 ? (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={meta.totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                />
              </Box>
            ) : null}
          </>
        )}
      </Box>
    </MDBox>
  );
}
